import React, { PropsWithChildren, useContext, useState, Fragment, useRef } from 'react';
import useStyles from '@front10/helpers/dist/UseStyles';
import Box from '../../../elements/Box';
import Checkbox from '../../../elements/Checkbox';
import LocationSelectorFilter from './../../LocationSelector';
import Button from '../../../elements/Button';
import Text from '../../../elements/Text';
import { CommonCalendarState, FlexibleDateRangeValue, FlexibleDateRangeValues } from '../types';
import { Context } from '@front10/helpers/dist/Context';
import { CalendarDispatchAction } from './hooks';
import { isDayAfter, isDayBefore } from './helper';

export type CalendarFooterProps = {
  enableFlexibleDates: boolean;
  checkedFlexibleDates: boolean;
  flexibleDatesRangeValues: FlexibleDateRangeValues;
  footerClearButtonComponent?: any;
  footerDoneButtonComponent?: any;
  footerCheckboxComponent?: any;
  footerContainerComponent?: any;
  footerSelectComponent?: any;
  style?: any;
  dispatch: CalendarDispatchAction;
  state: CommonCalendarState;
  footerSettings?: {
    elementsToShow: Array<string>;
    labelKeyInformation?: string;
  };
  footerInformationTextComponent?: Record<string, object>;
  onSaveFunction?: (value: boolean) => void;
  isMobile?: boolean;
  flexibleValue?: number;
  callToAction?: any;
  format?: string;
  firstAvailableDay?: number;
};

export const defaultFlexibleDates: FlexibleDateRangeValues = {
  values: [
    { name: 'cmp-flexible-dates-opt1', value: 1, key: 'value1' },
    { name: 'cmp-flexible-dates-opt2', value: 2, key: 'value2' },
    { name: 'cmp-flexible-dates-opt3', value: 3, key: 'value3' },
    { name: 'cmp-flexible-dates-opt4', value: 4, key: 'value4' },
    { name: 'cmp-flexible-dates-opt5', value: 5, key: 'value5' }
  ],
  defaultValue: 4
};

const CalendarFooter = (props: PropsWithChildren<CalendarFooterProps>) => {
  const {
    enableFlexibleDates = false,
    checkedFlexibleDates = false,
    flexibleDatesRangeValues = { ...defaultFlexibleDates },
    footerClearButtonComponent = {},
    footerDoneButtonComponent = {},
    footerCheckboxComponent = {},
    footerContainerComponent = {},
    footerSelectComponent = {},
    children,
    dispatch,
    state,
    footerSettings,
    footerInformationTextComponent,
    isMobile,
    flexibleValue,
    callToAction
  } = props;

  const [flexibleDatesValues, setFlexibleDatesValues] = useState(flexibleValue);
  const [activateDoneButton, setActivateDoneButton] = useState(false);
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const getOptions = (options: Array<FlexibleDateRangeValue>) => {
    return options.map((option: FlexibleDateRangeValue) => ({
      label: translate(option.name, option.name),
      value: option.value,
      key: option.key
    }));
  };

  const isClearDisabled =
    (state?.inputValue.start === undefined ||
      state?.inputValue.start === null ||
      state?.inputValue.start === '') &&
    (state?.inputValue.end === undefined ||
      state?.inputValue.end === null ||
      state?.inputValue.end === '');
  const isDoneDisabled =
    (state?.inputValue.start === undefined ||
      state?.inputValue.start === null ||
      state?.inputValue.start === '') &&
    (state?.inputValue.end === undefined ||
      state?.inputValue.end === null ||
      state?.inputValue.end === '') &&
    !activateDoneButton;

  const elements: Record<string, any> = {
    FlexibleDates: enableFlexibleDates && (
      <Fragment>
        <Checkbox
          {...footerCheckboxComponent}
          className="custom-checkbox"
          isChecked={checkedFlexibleDates}
          onChange={() => {
            // setIsCheckedFlexibleDates(!isCheckedFlexibleDates);
            dispatch({
              type: 'SET_FLEXIBLE_DATES_CHECKED',
              payload: !state.isCheckedFlexibleDates
            });
            dispatch({
              type: 'SET_USER_INTERACTION_ACTION',
              payload: true
            });
          }}
          label={translate('cmp-dates-picker-label-flexible-dates', 'Flexible dates')}
          tabIndex={0}
        />
        <LocationSelectorFilter
          showIconLeft={false}
          enableVirtualList={true}
          makeAccessibleByArrowsKeys={true}
          customAccessibility={true}
          {...footerSelectComponent}
          allowVariableHighlightedIndex={true}
          options={getOptions(flexibleDatesRangeValues.values)}
          defaultValue={flexibleDatesValues} //TODO: add this label
          ariaLabel={translate('cmp-dates-picker-flexible-dates-select', 'Flexible dates values')}
          onChange={(e: any) => {
            setFlexibleDatesValues(parseInt(e.value));
            dispatch({ type: 'SET_FLEXIBLE_DATES_VALUE', payload: e.value });
            dispatch({
              type: 'SET_USER_INTERACTION_ACTION',
              payload: true
            });
          }}
          tabIndex={0}
          selectMode={true}
          showSelectorMenuArrow={false}
          dataTestId="locationSelector-flexibleDates"
          placeholder={translate(
            'cmp-location-flexibles-dates-placeholder',
            'Select Flexibles Dates'
          )}
          labelKey="flexiblesDates"
          isMobile={isMobile}
        />
      </Fragment>
    ),
    InformationText: (
      <Text type="label" {...footerInformationTextComponent}>
        {translate(footerSettings?.labelKeyInformation ?? '', 'Information.')}
      </Text>
    )
  };

  const tmp = footerSettings?.elementsToShow ? [...footerSettings?.elementsToShow] : [];
  const doneButtonRef = useRef<any>(null);

  const alwaysEnabledDoneButton =
    footerDoneButtonComponent?.alwaysEnabledDoneButton === undefined
      ? false
      : footerDoneButtonComponent?.alwaysEnabledDoneButton;

  const enabledDoneButton =
    alwaysEnabledDoneButton === true
      ? false
      : state.userInteraction
      ? isDoneDisabled
        ? true
        : false
      : isDoneDisabled;

  const isEnabledDoneButton = tmp.find((e) => e === 'DoneButton');
  const isEnabledCancelButton = tmp.find((e) => e === 'CancelButton');
  const buttons = (
    <Box className="cmp-footer-buttons-container" display="flex" marginLeft="auto">
      {isEnabledCancelButton && (
        <Button
          onClick={() => {
            dispatch({ type: 'SET_SELECTED_INPUT', payload: 'start' });
            dispatch({ type: 'CLEAR_ACTION' });
            // setActivateDoneButton(true);
            dispatch({
              type: 'SET_USER_INTERACTION_ACTION',
              payload: true
            });
            if (doneButtonRef && doneButtonRef?.current) {
              doneButtonRef?.current?.focus();
            }
          }}
          {...footerClearButtonComponent}
          tabIndex={0}
          disabled={isClearDisabled}
        >
          {translate('cmp-dates-range-selector-clear-button', 'Clear')}
        </Button>
      )}
      {isEnabledDoneButton && (
        <Button
          {...footerDoneButtonComponent}
          onClick={() => {
            setActivateDoneButton(false);
            if (state.preSelected) {
              if (state.selectedRange == null || Object.keys(state.selectedRange).length === 0) {
                if (state.selectedComponent === 'end') {
                  dispatch({
                    type: 'SET_DATE_ACTION',
                    payload: state.preSelected,
                    component: 'start',
                    close: false
                  });
                }
                if (state.userInteraction) {
                  dispatch({
                    type: 'SET_DATE_ACTION',
                    payload: state.preSelected,
                    component: state.selectedComponent,
                    close: true
                  });
                }
              } else if (
                state.currentDate?.start &&
                state.selectedRange?.start !== state.preSelected
              ) {
                if (state.selectedComponent === 'end') {
                  if (isDayBefore(state.currentDate.start, state.preSelected)) {
                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: state.preSelected,
                      component: state.selectedComponent,
                      close: true
                    });
                    return;
                  } else {
                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: state.preSelected,
                      component: 'start',
                      close: false
                    });

                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: state.preSelected,
                      component: state.selectedComponent,
                      close: true
                    });
                    return;
                  }
                } else if (state.currentDate.end && state.selectedRange.end !== state.preSelected) {
                  if (isDayAfter(state.currentDate.end, state.preSelected)) {
                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: state.preSelected,
                      component: state.selectedComponent,
                      close: true
                    });
                    return;
                  } else {
                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: state.preSelected,
                      component: state.selectedComponent,
                      close: false
                    });
                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: null,
                      component: 'end',
                      close: true
                    });
                    return;
                  }
                }
              }
            }
            if (state.valid) {
              if (state.currentDate) {
                if (state.userInteraction) {
                  if (state.currentDate.start && state?.preSelected) {
                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: state?.preSelected,
                      component: 'start',
                      close: true
                    });
                  }
                  if (state.currentDate.end) {
                    dispatch({
                      type: 'SET_DATE_ACTION',
                      payload: state.currentDate.end,
                      component: 'end',
                      close: true
                    });
                  }
                }
              }
              if (
                state.selectedRange !== undefined &&
                state.selectedRange !== null &&
                Object.keys(state.selectedRange).length !== 0
              ) {
                if (state.selectedRange.start) {
                  dispatch({
                    type: 'SET_DATE_ACTION',
                    payload: state.selectedRange.start,
                    component: 'start',
                    close: true
                  });
                }
                if (
                  state?.currentDate?.start &&
                  state?.currentDate?.end === undefined &&
                  state?.selectedRange?.end === undefined &&
                  state?.preSelected
                ) {
                  dispatch({
                    type: 'SET_DATE_ACTION',
                    payload: state?.preSelected,
                    component: 'end',
                    close: true
                  });
                } else {
                  dispatch({
                    type: 'SET_DATE_ACTION',
                    payload: state.selectedRange.end !== undefined ? state.selectedRange.end : null,
                    component: 'end',
                    close: true
                  });
                }
              }
              if (props.onSaveFunction) {
                props.onSaveFunction(state.inputValue.start !== '');
              } else {
                dispatch({ type: 'CLOSE_POPOVER_ACTION' });
              }
              dispatch({
                type: 'SET_USER_INTERACTION_ACTION',
                payload: false
              });
            }
          }}
          tabIndex={0}
          disabled={enabledDoneButton}
          focusReferences={doneButtonRef}
        >
          {translate('cmp-dates-range-selector-done-button', 'Done')}
        </Button>
      )}
    </Box>
  );

  return (
    <Box dataTestId="calendar-footer" className={className} style={style}>
      <Box {...footerContainerComponent}>
        {tmp.map((e) => elements[e])}
        {!isMobile ? (
          buttons
        ) : !isEnabledDoneButton && !isEnabledCancelButton ? (
          <Button
            width="100"
            rounded="0"
            color="dark"
            textTransform="uppercase"
            {...callToAction}
            onClick={() => {
              if (
                state.preSelected &&
                state.inputValue.start !== '' &&
                (state.selectedRange == null || Object.keys(state.selectedRange).length === 0)
              ) {
                dispatch({
                  type: 'SET_DATE_ACTION',
                  payload: state.preSelected,
                  component: state.selectedComponent,
                  close: true
                });
              }
              if (props.onSaveFunction) {
                props.onSaveFunction(state.inputValue.start !== '');
              }
            }}
            disabled={!state.inputValue.start}
          >
            {translate('cmp-dates-range-selector-mobile-call-to-action', 'Select these days')}
          </Button>
        ) : (
          buttons
        )}
      </Box>
      {children}
    </Box>
  );
};

export default CalendarFooter;
